/**
 * LayoutThumbnailPicker
 * --------------------------------------------------------------
 * Renders a 4-card "shop window" of the available PDF layouts. Each card is a
 * live thumbnail (rasterised page 1 of a sample invoice) rendered in that
 * specific layout. Clicking a card calls `onChange(layout)` and highlights it
 * with a coloured ring.
 *
 * Usage:
 *   <LayoutThumbnailPicker value={layout} onChange={setLayout} />
 */
import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

const LAYOUTS = [
  { key: 'classic',      title: 'Classic',      blurb: 'Split header (default)' },
  { key: 'modern_band',  title: 'Modern Band',  blurb: 'Full-width coloured header' },
  { key: 'minimalist',   title: 'Minimalist',   blurb: 'Black & white + accent line' },
  { key: 'compact',      title: 'Compact',      blurb: 'Tighter spacing for long docs' },
];

function getToken() {
  return (
    localStorage.getItem('erp_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('token')
  );
}

function ThumbnailCard({ layout, title, blurb, selected, onPick }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(false);
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/preview/layout/${layout}/thumbnail.png`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) throw new Error('bad status');
        const blob = await res.blob();
        if (cancelled) return;
        setSrc(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) setErr(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [layout]);

  return (
    <button
      type="button"
      onClick={() => onPick(layout)}
      data-testid={`layout-thumb-${layout}`}
      className={`group relative flex flex-col rounded-md border bg-card transition-all overflow-hidden text-left ${
        selected
          ? 'border-primary ring-2 ring-primary/50 shadow-md'
          : 'border-input hover:border-primary/60 hover:shadow-sm'
      }`}
    >
      {selected && (
        <span className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
          <Check size={10} /> Selected
        </span>
      )}
      <div className="aspect-[1/1.41] w-full bg-muted/40 flex items-center justify-center overflow-hidden">
        {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        {err && <span className="text-xs text-red-500">Preview failed</span>}
        {src && (
          <img
            src={src}
            alt={`${title} layout preview`}
            className="h-full w-full object-cover object-top transition-transform group-hover:scale-[1.02]"
          />
        )}
      </div>
      <div className="px-2.5 py-1.5 border-t bg-background">
        <div className="text-xs font-semibold truncate">{title}</div>
        <div className="text-[10px] text-muted-foreground truncate">{blurb}</div>
      </div>
    </button>
  );
}

export default function LayoutThumbnailPicker({ value, onChange }) {
  const selected = value || 'classic';
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="layout-thumbnail-picker">
      {LAYOUTS.map((l) => (
        <ThumbnailCard
          key={l.key}
          layout={l.key}
          title={l.title}
          blurb={l.blurb}
          selected={selected === l.key}
          onPick={onChange}
        />
      ))}
    </div>
  );
}
